import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
    Bot, ExternalLink, Flag, Globe, Mail, MessageSquare, RefreshCw, Scale, Server, TriangleAlert, UserRound, Wrench, X, Zap,
} from 'lucide-react';
import { fmtMs, fmtCost, fmtClock, fmtDateTime, compactJson, prettyPayload, statusClass, spanHeadline, spanTitle, runTitle, runStatusLabel } from './aiTrace/format';
import './AiTimelineOverlay.css';

// Full-screen chronology of a ticket's AI trace (docs/ai-trace-spec.md §13,
// step 14): every run of the ticket laid out left → right in time, one card
// per step (model call, tool, decision, effect, external call, automation,
// human feedback); the runs another service spawned (mail) branch off the
// step that started them and run on their own row; a card opens a drawer
// with its attributes, payloads and, for carrier automations, the captures.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const KIND_ICONS = {
    run: Flag, llm: Bot, tool: Wrench, decision: Scale, effect: Zap, automation: Mail,
    feedback: MessageSquare, external: Globe, error: TriangleAlert,
};
const ALL_KINDS = ['llm', 'tool', 'decision', 'effect', 'external', 'automation', 'feedback', 'error'];
const CARD_W = 232;
const CARD_H = 118;
const GAP_X = 28;
const GAP_Y = 34;
const PAD = 24;

// ----------------------------------------------------------------------
// Layout: runs → rows of cards with absolute positions and connectors
// ----------------------------------------------------------------------

const buildLayout = (timeline, hiddenKinds, showRelated) => {
    const all = [...(timeline.runs || []), ...(timeline.related_runs || [])];
    const byId = new Map(all.map((r) => [r._id, r]));
    const childrenOf = new Map();
    all.forEach((r) => {
        if (r.parent_run_id && byId.has(r.parent_run_id)) {
            if (!childrenOf.has(r.parent_run_id)) childrenOf.set(r.parent_run_id, []);
            childrenOf.get(r.parent_run_id).push(r);
        }
    });
    childrenOf.forEach((list) => list.sort((a, b) => new Date(a.started_at) - new Date(b.started_at)));
    const topRuns = all
        .filter((r) => !(r.parent_run_id && byId.has(r.parent_run_id)))
        .filter((r) => showRelated || (timeline.runs || []).some((x) => x._id === r._id))
        .sort((a, b) => new Date(a.started_at) - new Date(b.started_at));

    const cards = [];
    const links = [];
    const rowsUsed = []; // rowsUsed[row] = list of [startCol, endCol] ranges
    const reserve = (minRow, startCol, endCol) => {
        let row = minRow;
        for (;;) {
            const ranges = rowsUsed[row] || [];
            if (!ranges.some(([s, e]) => !(endCol < s || startCol > e))) {
                rowsUsed[row] = [...ranges, [startCol, endCol]];
                return row;
            }
            row += 1;
        }
    };

    let col = 0;
    const placeRun = (run, startCol, minRow, anchorCardId, depth) => {
        const spans = (run.spans || []).filter((s) => !hiddenKinds.has(s.kind));
        const width = spans.length + 1;
        const row = reserve(minRow, startCol, startCol + width - 1);
        const headerId = `run:${run._id}`;
        cards.push({ id: headerId, type: 'run', run, col: startCol, row, depth });
        if (anchorCardId) links.push({ from: anchorCardId, to: headerId, branch: true });
        let prev = headerId;
        const spanCols = new Map();
        spans.forEach((span, i) => {
            const id = `span:${span._id}`;
            cards.push({ id, type: 'span', span, run, col: startCol + 1 + i, row, depth });
            links.push({ from: prev, to: id, branch: false });
            spanCols.set(span._id, startCol + 1 + i);
            prev = id;
        });
        const endCol = startCol + width - 1;
        (childrenOf.get(run._id) || []).forEach((child) => {
            // anchor: the last step started before the child run started
            const started = new Date(child.started_at).getTime();
            let anchorId = headerId;
            let anchorCol = startCol;
            spans.forEach((s) => {
                const t = new Date(s.started_at).getTime();
                if (t <= started) { anchorId = `span:${s._id}`; anchorCol = spanCols.get(s._id); }
            });
            placeRun(child, anchorCol + 1, row + 1, anchorId, depth + 1);
        });
        return endCol;
    };

    topRuns.forEach((run) => {
        const endCol = placeRun(run, col, 0, null, 0);
        // the next top-level run starts after everything placed so far
        const maxEnd = Math.max(endCol, ...rowsUsed.flatMap((ranges) => (ranges || []).map(([, e]) => e)));
        col = maxEnd + 2;
    });
    const maxCol = cards.reduce((m, c) => Math.max(m, c.col), 0);
    const maxRow = cards.reduce((m, c) => Math.max(m, c.row), 0);
    return { cards, links, byId, childrenOf, width: PAD * 2 + (maxCol + 1) * (CARD_W + GAP_X), height: PAD * 2 + (maxRow + 1) * (CARD_H + GAP_Y) };
};

const cardX = (card) => PAD + card.col * (CARD_W + GAP_X);
const cardY = (card) => PAD + card.row * (CARD_H + GAP_Y);

// ----------------------------------------------------------------------

const AiTimelineOverlay = ({ ticketId, ticketSubject, focusRunId, focusSpanId, focusKey, onClose }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [timeline, setTimeline] = useState(null);
    const [error, setError] = useState(null);
    const [hiddenKinds, setHiddenKinds] = useState(() => new Set());
    const [showRelated, setShowRelated] = useState(true);
    const [selected, setSelected] = useState(null); // card id
    const [payloads, setPayloads] = useState({});
    const canvasRef = useRef(null);
    const appliedFocusRef = useRef(null); // focusKey already applied: filters must not re-jump

    const load = useCallback(async () => {
        if (!ticketId) return;
        setError(null);
        try {
            const response = await axios.get(`/api/tickets/${ticketId}/ai-timeline?limit=100`, authHeaders());
            setTimeline(response.data);
        } catch (e) {
            setTimeline({ runs: [], related_runs: [], debug_runs: [] });
            setError(t('aiRunPanel.loadError', 'Impossible de charger les runs IA.'));
        }
    }, [ticketId, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const layout = useMemo(() => (timeline ? buildLayout(timeline, hiddenKinds, showRelated) : null), [timeline, hiddenKinds, showRelated]);
    const cardsById = useMemo(() => new Map((layout?.cards || []).map((c) => [c.id, c])), [layout]);
    const debugByRun = useMemo(() => {
        const map = new Map();
        (timeline?.debug_runs || []).forEach((d) => {
            if (!map.has(d.trace_run_id)) map.set(d.trace_run_id, []);
            map.get(d.trace_run_id).push(d);
        });
        return map;
    }, [timeline]);
    const debugById = useMemo(() => new Map((timeline?.debug_runs || []).map((d) => [d.debug_run_id, d])), [timeline]);

    // focus: select and scroll to the card the caller pointed at
    useEffect(() => {
        if (!layout || appliedFocusRef.current === focusKey) return;
        appliedFocusRef.current = focusKey;
        const target = (focusSpanId && cardsById.has(`span:${focusSpanId}`) && `span:${focusSpanId}`)
            || (focusRunId && cardsById.has(`run:${focusRunId}`) && `run:${focusRunId}`) || null;
        if (!target) return;
        setSelected(target);
        const card = cardsById.get(target);
        const el = canvasRef.current;
        if (el && card) {
            el.scrollTo({ left: Math.max(0, cardX(card) - el.clientWidth / 3), top: Math.max(0, cardY(card) - 80), behavior: 'smooth' });
        }
    }, [layout, cardsById, focusRunId, focusSpanId, focusKey]);

    const toggleKind = (kind) => setHiddenKinds((prev) => {
        const next = new Set(prev);
        if (next.has(kind)) next.delete(kind); else next.add(kind);
        return next;
    });

    const loadPayload = useCallback(async (runId, spanId, role) => {
        const key = `${spanId}:${role}`;
        setPayloads((prev) => ({ ...prev, [key]: { loading: true } }));
        try {
            const response = await axios.get(`/api/ai-runs/${runId}/spans/${spanId}/payload/${role}`,
                { ...authHeaders(), responseType: 'text', transformResponse: [(data) => data] });
            setPayloads((prev) => ({ ...prev, [key]: { text: typeof response.data === 'string' ? response.data : JSON.stringify(response.data),
                truncated: String(response.headers['x-truncated']) === 'true' } }));
        } catch (e) {
            setPayloads((prev) => ({ ...prev, [key]: { error: e?.response?.status === 404
                ? t('aiRunPanel.payloadMissing', 'Contenu non conservé ou expiré.') : t('aiRunPanel.loadError', 'Impossible de charger les runs IA.') } }));
        }
    }, [t]);

    // ------------------------------------------------------------------
    // Renderers
    // ------------------------------------------------------------------

    const renderCard = (card) => {
        const isSelected = selected === card.id;
        if (card.type === 'run') {
            const run = card.run;
            const Icon = run.trigger?.actor?.type === 'employee' || run.trigger?.actor?.type === 'admin' ? UserRound : Flag;
            return (
                <button
                    key={card.id}
                    type="button"
                    className={`atl-card atl-card-run atl-service-${run.service} ${isSelected ? 'selected' : ''} ${statusClass(run.status)}`}
                    style={{ left: cardX(card), top: cardY(card), width: CARD_W, height: CARD_H }}
                    onClick={() => setSelected(card.id)}
                    title={run._id}
                >
                    <div className="atl-card-head">
                        <span className="atl-kind atl-kind-run"><Icon /> {t(`aiTimeline.run.${run.trigger?.kind}`, t(`aiRunPanel.trigger.${run.trigger?.kind}`, run.trigger?.kind || 'run'))}</span>
                        <span className={`atl-pill atl-pill-${statusClass(run.status)}`}>{runStatusLabel(run.status, t)}</span>
                    </div>
                    <div className="atl-card-title">{run.trigger?.source}</div>
                    <div className="atl-card-line">
                        {run.trigger?.actor?.type && run.trigger.actor.type !== 'system' ? `${run.trigger.actor.type} · ` : ''}
                        {run.service} · {(run.spans || []).length} {t('aiTimeline.steps', 'étapes')}
                    </div>
                    <div className="atl-card-foot">
                        <span>{fmtClock(run.started_at, lang)}</span>
                        <span>{fmtMs(run.duration_ms)}</span>
                        {fmtCost(run.summary?.total_cost_usd) && <span>{fmtCost(run.summary?.total_cost_usd)}</span>}
                    </div>
                </button>
            );
        }
        const span = card.span;
        const Icon = KIND_ICONS[span.kind] || TriangleAlert;
        return (
            <button
                key={card.id}
                type="button"
                className={`atl-card atl-card-${span.kind} ${isSelected ? 'selected' : ''} ${statusClass(span.status)} ${span.late ? 'late' : ''}`}
                style={{ left: cardX(card), top: cardY(card), width: CARD_W, height: CARD_H }}
                onClick={() => setSelected(card.id)}
                title={span._id}
            >
                <div className="atl-card-head">
                    <span className={`atl-kind atl-kind-${span.kind}`}><Icon /> {t(`aiRunPanel.kind.${span.kind}`, span.kind)}</span>
                    <span className={`atl-pill atl-pill-${statusClass(span.status)}`}>{runStatusLabel(span.status, t)}</span>
                </div>
                <div className="atl-card-title">{spanTitle(span, t)}</div>
                <div className="atl-card-line">{spanHeadline(span, t, lang)}</div>
                <div className="atl-card-foot">
                    <span>{fmtClock(span.started_at, lang)}</span>
                    <span>{fmtMs(span.duration_ms)}</span>
                    {((span.attrs?.automation?.debug_run_id && debugById.get(span.attrs.automation.debug_run_id)?.artifacts?.screenshot)
                        || span.attrs?.portal?.screenshot_url) && (
                        <span className="atl-has-capture">{t('aiTimeline.capture', 'capture')}</span>
                    )}
                </div>
            </button>
        );
    };

    const renderLinks = () => {
        if (!layout) return null;
        return (
            <svg className="atl-links" width={layout.width} height={layout.height}>
                {layout.links.map((link) => {
                    const from = cardsById.get(link.from);
                    const to = cardsById.get(link.to);
                    if (!from || !to) return null;
                    if (!link.branch) {
                        const x1 = cardX(from) + CARD_W; const y1 = cardY(from) + CARD_H / 2;
                        const x2 = cardX(to); const y2 = cardY(to) + CARD_H / 2;
                        return <path key={`${link.from}-${link.to}`} className="atl-link" d={`M${x1},${y1} L${x2},${y2}`} />;
                    }
                    const x1 = cardX(from) + CARD_W / 2; const y1 = cardY(from) + CARD_H;
                    const x2 = cardX(to); const y2 = cardY(to) + CARD_H / 2;
                    return <path key={`${link.from}-${link.to}`} className="atl-link atl-link-branch" d={`M${x1},${y1} L${x1},${y2} L${x2},${y2}`} />;
                })}
            </svg>
        );
    };

    // Context builder report (backend plan H3): one row per section with its

    // token use against its budget, what was cut, and where the text came from.

    const renderContextSections = (report) => {

        const maxTokens = Math.max(1, ...report.sections.map((s) => s.tokens || 0));

        const provenanceOf = (p) => Object.entries(p || {}).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ');

        const history = report.history || {};

        return (

            <div className="atl-context">

                <div className="atl-context-summary">

                    {(report.total_tokens || 0).toLocaleString(lang)}{report.budget ? ` / ${report.budget.toLocaleString(lang)}` : ''} {t('aiRunPanel.counters.tokens', 'tokens')}

                    {report.over_budget && <span className="atl-context-flag">{t('aiTimeline.context.overBudget', 'hors budget')}</span>}

                    {history.budget !== undefined && (

                        <span className="atl-muted"> · {t('aiTimeline.context.history', 'historique')} {(history.kept_tokens || 0).toLocaleString(lang)} / {(history.budget || 0).toLocaleString(lang)}{history.dropped_items ? ` (${history.dropped_items} ${t('aiTimeline.context.historyDropped', 'tours compactés')}${history.summarized ? `, ${t('aiTimeline.context.summarized', 'résumés')}` : ''})` : ''}</span>

                    )}

                </div>

                {report.sections.map((s) => (

                    <div key={s.key} className={`atl-context-row ${s.dropped ? 'dropped' : ''} ${s.empty ? 'empty' : ''}`} title={provenanceOf(s.provenance)}>

                        <span className="atl-context-key">{t(`aiTimeline.context.section.${s.key}`, s.key.replace(/_/g, ' '))}</span>

                        <span className="atl-context-bar"><span className="atl-context-fill" style={{ width: `${Math.round((((s.kept_tokens !== undefined ? s.kept_tokens : s.tokens) || 0) / maxTokens) * 100)}%` }} /></span>

                        <span className="atl-context-tokens">

                            {s.empty ? '—' : `${((s.kept_tokens !== undefined ? s.kept_tokens : s.tokens) || 0).toLocaleString(lang)}${s.truncated ? ` / ${(s.tokens || 0).toLocaleString(lang)}` : ''}`}

                            {s.budget ? <span className="atl-muted"> ({t('aiTimeline.context.budget', 'budget')} {s.budget.toLocaleString(lang)})</span> : null}

                        </span>

                        <span className="atl-context-state">

                            {s.dropped ? t('aiTimeline.context.stateDropped', 'retirée') : (s.truncated ? t('aiTimeline.context.stateTruncated', 'tronquée') : (s.empty ? t('aiTimeline.context.stateEmpty', 'vide') : ''))}

                        </span>

                        <span className="atl-context-provenance">{s.provenance?.source || ''}</span>

                    </div>

                ))}

            </div>

        );

    };


    const renderPayloads = (runId, span) => {
        const refs = span.payload_refs || {};
        const roles = Object.keys(refs);
        if (!roles.length) return null;
        return (
            <div className="atl-section">
                <div className="atl-section-title">{t('aiTimeline.payloads', 'Contenus')}</div>
                <div className="atl-chips">
                    {roles.map((role) => {
                        const key = `${span._id}:${role}`;
                        const state = payloads[key];
                        return (
                            <button key={role} type="button" className={`atl-chip ${state?.text ? 'active' : ''}`} disabled={state?.loading}
                                onClick={() => (state?.text ? setPayloads((p) => ({ ...p, [key]: undefined })) : loadPayload(runId, span._id, role))}>
                                {t(`aiRunPanel.payload.${role}`, role)} <span className="atl-muted">{refs[role]?.bytes ? `${(refs[role].bytes / 1024).toFixed(1)} Ko` : ''}</span>
                            </button>
                        );
                    })}
                </div>
                {roles.map((role) => {
                    const state = payloads[`${span._id}:${role}`];
                    if (!state) return null;
                    return (
                        <div key={role} className="atl-payload">
                            <div className="atl-payload-title">{t(`aiRunPanel.payload.${role}`, role)} {state.truncated && <span className="atl-pill atl-pill-warn">{t('aiRunPanel.truncated', 'tronqué')}</span>}</div>
                            {state.loading && <div className="atl-muted">{t('aiRunPanel.loading', 'Chargement…')}</div>}
                            {state.error && <div className="atl-error">{state.error}</div>}
                            {state.text !== undefined && <pre className="atl-json">{prettyPayload(state.text)}</pre>}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderDebug = (dbg) => (
        <div key={dbg.debug_run_id} className="atl-debug">
            <div className="atl-debug-head">
                <span>{dbg.carrier} · {dbg.task_type || dbg.workflow}</span>
                <span className={`atl-pill atl-pill-${statusClass(dbg.status)}`}>{runStatusLabel(dbg.status, t)}</span>
                <span className="atl-muted">{fmtMs(dbg.duration_ms)}</span>
            </div>
            {dbg.failure_category && <div className="atl-error">{dbg.failure_category}{dbg.error ? ` — ${compactJson(dbg.error, 300)}` : ''}</div>}
            {dbg.artifacts?.screenshot && (
                <a href={dbg.artifacts.screenshot} target="_blank" rel="noopener noreferrer" className="atl-capture">
                    <img src={dbg.artifacts.screenshot} alt={t('aiTimeline.captureAlt', 'Capture du portail transporteur')} loading="lazy" />
                </a>
            )}
            <div className="atl-chips">
                {dbg.artifacts?.screenshot && <a className="atl-chip" href={dbg.artifacts.screenshot} target="_blank" rel="noopener noreferrer"><ExternalLink aria-hidden="true" /> {t('aiRunPanel.screenshot', 'capture')}</a>}
                {dbg.artifacts?.page_source && <a className="atl-chip" href={dbg.artifacts.page_source} target="_blank" rel="noopener noreferrer"><ExternalLink aria-hidden="true" /> {t('aiRunPanel.pageSource', 'page HTML')}</a>}
            </div>
        </div>
    );

    const renderDrawer = () => {
        const card = selected ? cardsById.get(selected) : null;
        if (!card) {
            return <div className="atl-drawer-empty">{t('aiTimeline.pickCard', 'Cliquez sur une étape pour voir son détail.')}</div>;
        }
        if (card.type === 'run') {
            const run = card.run;
            const debugs = debugByRun.get(run._id) || [];
            const summary = run.summary || {};
            return (
                <div className="atl-drawer-body">
                    <h4><Flag aria-hidden="true" /> {runTitle(run, t)}</h4>
                    <div className="atl-facts">
                        <span><Server aria-hidden="true" /> {run.service}</span>
                        <span>{fmtDateTime(run.started_at, lang)}</span>
                        <span>{fmtMs(run.duration_ms)}</span>
                        <span className={`atl-pill atl-pill-${statusClass(run.status)}`}>{runStatusLabel(run.status, t)}</span>
                        {run.trigger?.actor?.type && run.trigger.actor.type !== 'system' && <span><UserRound aria-hidden="true" /> {run.trigger.actor.type}</span>}
                        {run.versions?.app_release && <span className="atl-muted">{String(run.versions.app_release).slice(0, 8)}</span>}
                    </div>
                    <div className="atl-chips">
                        {['llm_calls', 'tool_calls', 'decisions', 'effects', 'externals', 'automations', 'feedback', 'errors']
                            .filter((k) => summary[k] > 0)
                            .map((k) => <span key={k} className={`atl-chip ${k === 'errors' ? 'error' : ''}`}>{summary[k]} {t(`aiTimeline.counter.${k}`, k)}</span>)}
                        {fmtCost(summary.total_cost_usd) && <span className="atl-chip">{fmtCost(summary.total_cost_usd)}</span>}
                    </div>
                    {run.error && <div className="atl-error"><TriangleAlert aria-hidden="true" /> {run.error.type}: {run.error.message}</div>}
                    {summary.outcome && Object.keys(summary.outcome).length > 0 && (
                        <details className="atl-section atl-details"><summary className="atl-section-title">{t('aiRunPanel.outcome', 'résultat')}</summary><pre className="atl-json">{JSON.stringify(summary.outcome, null, 2)}</pre></details>
                    )}
                    {run.links && Object.keys(run.links).length > 0 && (
                        <div className="atl-section"><div className="atl-section-title">{t('aiTimeline.links', 'Runs liés')}</div>
                            <div className="atl-chips">{Object.entries(run.links).map(([k, v]) => (
                                <button key={k} type="button" className="atl-chip" onClick={() => cardsById.has(`run:${v}`) && setSelected(`run:${v}`)}>{t(`aiRunPanel.link.${k}`, k)}</button>
                            ))}</div>
                        </div>
                    )}
                    {debugs.length > 0 && <div className="atl-section"><div className="atl-section-title">{t('aiRunPanel.debugRuns', 'Automatisations transporteur')}</div>{debugs.map(renderDebug)}</div>}
                </div>
            );
        }
        const span = card.span;
        const debug = span.attrs?.automation?.debug_run_id ? debugById.get(span.attrs.automation.debug_run_id) : null;
        const Icon = KIND_ICONS[span.kind] || TriangleAlert;
        return (
            <div className="atl-drawer-body">
                <h4><Icon /> {spanTitle(span, t)}</h4>
                <div className="atl-facts">
                    <span className={`atl-kind atl-kind-${span.kind}`}>{t(`aiRunPanel.kind.${span.kind}`, span.kind)}</span>
                    <span>{fmtDateTime(span.started_at, lang)}</span>
                    <span>{fmtMs(span.duration_ms)}</span>
                    <span className={`atl-pill atl-pill-${statusClass(span.status)}`}>{runStatusLabel(span.status, t)}</span>
                    {span.operation && <span className="atl-muted">{span.operation}</span>}
                    {span.late && <span className="atl-pill atl-pill-warn">{t('aiRunPanel.late', 'tardif')}</span>}
                </div>
                <div className="atl-headline">{spanHeadline(span, t, lang)}</div>
                {span.error && <div className="atl-error"><TriangleAlert aria-hidden="true" /> {span.error.type}: {span.error.message}</div>}
                {debug && <div className="atl-section"><div className="atl-section-title">{t('aiRunPanel.debugRuns', 'Automatisations transporteur')}</div>{renderDebug(debug)}</div>}
                {span.attrs?.portal?.screenshot_url && (
                    <div className="atl-section">
                        <div className="atl-section-title">{t('aiTimeline.portalCapture', 'Capture du portail à cette étape')}</div>
                        {span.attrs.portal.url && <div className="atl-muted">{span.attrs.portal.url}</div>}
                        <a href={span.attrs.portal.screenshot_url} target="_blank" rel="noopener noreferrer" className="atl-capture">
                            <img src={span.attrs.portal.screenshot_url} alt={t('aiTimeline.captureAlt', 'Capture du portail transporteur')} loading="lazy" />
                        </a>
                    </div>
                )}
                {span.attrs?.decision?.agent === 'context_builder' && Array.isArray(span.attrs.decision.outcome?.sections) && (
                    <div className="atl-section">
                        <div className="atl-section-title">{t('aiTimeline.context.title', 'Sections du contexte')}</div>
                        {renderContextSections(span.attrs.decision.outcome)}
                    </div>
                )}
                {span.attrs && Object.keys(span.attrs).length > 0 && (
                    <details className="atl-section atl-details"><summary className="atl-section-title">{t('aiTimeline.attributes', 'Attributs')}</summary><pre className="atl-json">{JSON.stringify(span.attrs, null, 2)}</pre></details>
                )}
                {renderPayloads(card.run._id, span)}
            </div>
        );
    };

    const runCount = (timeline?.runs || []).length;

    return (
        <div className="ai-timeline-overlay" role="dialog" aria-modal="true" aria-label={t('aiTimeline.title', 'Chronologie IA du ticket')}>
            <div className="atl-topbar">
                <div className="atl-topbar-main">
                    <h3>{t('aiTimeline.title', 'Chronologie IA du ticket')}</h3>
                    {ticketSubject && <span className="atl-subject" title={ticketSubject}>{ticketSubject}</span>}
                    {timeline && <span className="atl-muted">{runCount} {t('aiTimeline.runs', 'runs')}</span>}
                    {timeline?.truncated && <span className="atl-pill atl-pill-warn">{t('aiRunPanel.truncated', 'tronqué')}</span>}
                </div>
                <div className="atl-topbar-actions">
                    <button type="button" className="u-btn u-btn--ghost u-btn--icon" onClick={load} title={t('aiRunPanel.refresh', 'Actualiser')} aria-label={t('aiRunPanel.refresh', 'Actualiser')}><RefreshCw strokeWidth={1.8} /></button>
                    <button type="button" className="u-btn u-btn--secondary u-btn--icon" onClick={onClose} title={t('aiTimeline.close', 'Fermer')} aria-label={t('aiTimeline.close', 'Fermer')}><X strokeWidth={2} /></button>
                </div>
            </div>
            <div className="atl-filters">
                {ALL_KINDS.map((kind) => {
                    const Icon = KIND_ICONS[kind];
                    return (
                        <button key={kind} type="button" className={`atl-filter atl-kind-${kind} ${hiddenKinds.has(kind) ? 'off' : ''}`} onClick={() => toggleKind(kind)} aria-pressed={!hiddenKinds.has(kind)}>
                            <Icon /> {t(`aiRunPanel.kind.${kind}`, kind)}
                        </button>
                    );
                })}
                <label className="atl-toggle">
                    <input type="checkbox" checked={showRelated} onChange={(e) => setShowRelated(e.target.checked)} />
                    {t('aiTimeline.showRelated', 'Runs liés (service mail)')}
                </label>
                <span className="atl-hint">{t('aiTimeline.hint', 'Les runs se lisent de gauche à droite ; une branche descend depuis l’étape qui l’a déclenchée.')}</span>
            </div>
            <div className="atl-body">
                <div className="atl-canvas" ref={canvasRef}>
                    {error && <div className="atl-error atl-floating">{error}</div>}
                    {!timeline && !error && <div className="atl-muted atl-floating">{t('aiRunPanel.loading', 'Chargement…')}</div>}
                    {timeline && runCount === 0 && !error && <div className="atl-muted atl-floating">{t('aiRunPanel.empty', 'Aucun run IA enregistré pour ce ticket.')}</div>}
                    {layout && (
                        <div className="atl-stage" style={{ width: layout.width, height: layout.height }}>
                            {renderLinks()}
                            {layout.cards.map(renderCard)}
                        </div>
                    )}
                </div>
                <aside className="atl-drawer">{renderDrawer()}</aside>
            </div>
        </div>
    );
};

export default AiTimelineOverlay;
